import React, { useEffect, useMemo, useRef, useState } from "react";
import { useEditorStore, useEditorStoreApi } from "../store/editorStoreSelectors";
import { useInkSpritesSetting } from "../hooks/useInkSpritesSetting";
import { buildGutterNetwork, crossingsOf, fuzzPath, pointOn } from "../utils/inkSprites";

/**
 * Ink sprites: a few soot-sprite inkblots that live in the gutters.
 *
 * They only come out when you've paused, wander the gutters, blink, and
 * glance at your cursor; get close (or click, type, scroll) and they scurry
 * off. They never take pointer events, never appear in exports or
 * thumbnails, and stay away entirely under reduced motion or when switched
 * off on the zoom card.
 */

const COUNT = 3;
const IDLE_MS = 15000;
const EAGER_IDLE_MS = 1200;
const WALK_SPEED = 26; // page px / s
const FLEE_SPEED = 190;
const FLEE_MS = 450;
const EMERGE_MS = 450;
const SCARE_DISTANCE = 70;
const LOOK_DISTANCE = 260;

const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const randBetween = (a, b) => a + Math.random() * (b - a);

const InkSprites = ({ pageRef, pageWidth, pageHeight }) => {
  const storeApi = useEditorStoreApi();
  const cells = useEditorStore((st) => st.cells);
  const gutterSize = useEditorStore((st) => st.gutterSize);
  const setting = useInkSpritesSetting();
  const enabled = setting !== "off" && !prefersReducedMotion();
  const idleMs = setting === "eager" ? EAGER_IDLE_MS : IDLE_MS;

  const radius = Math.max(4, Math.min(7.5, gutterSize * 0.42 + 1.5));
  const network = useMemo(
    () => buildGutterNetwork(cells, gutterSize, pageWidth, pageHeight),
    [cells, gutterSize, pageWidth, pageHeight]
  );
  const networkRef = useRef(network);
  networkRef.current = network;

  const sprites = useRef(
    Array.from({ length: COUNT }, (_, i) => ({ id: i, mode: "hidden", seed: 7919 * (i + 3) }))
  );
  const pointer = useRef(null); // page coords
  const lastActivity = useRef(Date.now());
  const [, setFrame] = useState(0);
  const rafRef = useRef(null);

  const fuzz = useMemo(
    () => sprites.current.map((s) => fuzzPath(radius, s.seed)),
    [radius]
  );

  // ─── Behaviour ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled) {
      sprites.current.forEach((s) => { s.mode = "hidden"; });
      setFrame((f) => f + 1);
      return undefined;
    }

    const toPage = (event) => {
      const page = pageRef.current;
      if (!page) return null;
      const rect = page.getBoundingClientRect();
      const z = storeApi.getState().zoom || 1;
      return { x: (event.clientX - rect.left) / z - page.clientLeft, y: (event.clientY - rect.top) / z - page.clientTop };
    };

    const scatter = () => {
      sprites.current.forEach((s) => {
        if (s.mode !== "hidden" && s.mode !== "fleeing") startFlee(s);
      });
    };

    const startFlee = (s) => {
      const seg = networkRef.current[s.seg];
      if (!seg) { s.mode = "hidden"; return; }
      const p = pointer.current;
      const here = pointOn(seg, s.t);
      let dir = Math.random() < 0.5 ? -1 : 1;
      if (p) {
        const along = seg.axis === "v" ? here.y - p.y : here.x - p.x;
        dir = along >= 0 ? 1 : -1;
      }
      s.mode = "fleeing";
      s.dir = dir;
      s.modeStart = performance.now();
    };

    const spawn = (s, now) => {
      const segs = networkRef.current;
      if (!segs.length) return;
      // Emerge from a margin when there is one, otherwise any gutter
      const margins = segs.map((seg, i) => i).slice(0, Math.min(4, segs.length));
      s.seg = margins[Math.floor(Math.random() * margins.length)];
      const seg = segs[s.seg];
      s.t = randBetween(seg.start + 40, seg.end - 40);
      s.mode = "emerging";
      s.modeStart = now;
      s.dir = 1;
      s.blinkUntil = 0;
      s.nextBlink = now + randBetween(1500, 4000);
      s.look = { x: 0, y: 0 };
    };

    const planLeg = (s, now) => {
      const segs = networkRef.current;
      const seg = segs[s.seg];
      if (!seg) { s.mode = "hidden"; return; }
      const crossings = crossingsOf(segs, s.seg).filter((c) => Math.abs(c.t - s.t) > 12);
      if (crossings.length && Math.random() < 0.55) {
        const c = crossings[Math.floor(Math.random() * crossings.length)];
        s.target = c.t;
        s.turnTo = c.j;
      } else {
        let target = randBetween(seg.start + radius, seg.end - radius);
        if (Math.abs(target - s.t) < 30) target = s.t + (target > s.t ? 30 : -30);
        s.target = Math.max(seg.start + radius, Math.min(seg.end - radius, target));
        s.turnTo = null;
      }
      s.dir = s.target >= s.t ? 1 : -1;
      s.mode = "walking";
      s.modeStart = now;
    };

    let last = performance.now();
    const tick = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const segs = networkRef.current;
      let anyVisible = false;

      sprites.current.forEach((s) => {
        if (s.mode === "hidden") return;
        const seg = segs[s.seg];
        if (!seg) { s.mode = "hidden"; return; }
        s.t = Math.max(seg.start, Math.min(seg.end, s.t));
        anyVisible = true;
        const here = pointOn(seg, s.t);

        // Too close to the cursor: scurry
        const p = pointer.current;
        if (p && s.mode !== "fleeing" && Math.hypot(p.x - here.x, p.y - here.y) < SCARE_DISTANCE) startFlee(s);

        if (s.mode === "emerging" && now - s.modeStart > EMERGE_MS) {
          s.mode = "resting";
          s.modeStart = now;
          s.restFor = randBetween(600, 1800);
        } else if (s.mode === "resting" && now - s.modeStart > s.restFor) {
          planLeg(s, now);
        } else if (s.mode === "walking") {
          const step = WALK_SPEED * dt * s.dir;
          const arrived = (s.dir > 0 && s.t + step >= s.target) || (s.dir < 0 && s.t + step <= s.target);
          if (arrived) {
            s.t = s.target;
            if (s.turnTo != null && segs[s.turnTo]) {
              const point = pointOn(seg, s.t);
              const next = segs[s.turnTo];
              s.seg = s.turnTo;
              s.t = next.axis === "v" ? point.y : point.x;
            }
            s.mode = "resting";
            s.modeStart = now;
            s.restFor = randBetween(700, 2600);
          } else {
            s.t += step;
          }
        } else if (s.mode === "fleeing") {
          s.t += FLEE_SPEED * dt * s.dir;
          if (now - s.modeStart > FLEE_MS) s.mode = "hidden";
        }

        // Blink now and then
        if (now > s.nextBlink) {
          s.blinkUntil = now + 130;
          s.nextBlink = now + randBetween(2500, 6000);
        }
        // Eyes: the cursor if it's nearby, else where they're going
        if (p && Math.hypot(p.x - here.x, p.y - here.y) < LOOK_DISTANCE) {
          const d = Math.hypot(p.x - here.x, p.y - here.y) || 1;
          s.look = { x: (p.x - here.x) / d, y: (p.y - here.y) / d };
        } else if (s.mode === "walking") {
          s.look = seg.axis === "v" ? { x: 0, y: s.dir } : { x: s.dir, y: 0 };
        }
      });

      setFrame((f) => (f + 1) % 1000000);
      rafRef.current = anyVisible ? requestAnimationFrame(tick) : null;
    };

    const ensureRunning = () => {
      if (!rafRef.current) {
        last = performance.now();
        rafRef.current = requestAnimationFrame(tick);
      }
    };

    // Come out after a pause, a little staggered
    const idleCheck = window.setInterval(() => {
      if (document.hidden) return;
      const now = Date.now();
      if (now - lastActivity.current < idleMs) return;
      const hidden = sprites.current.filter((s) => s.mode === "hidden");
      if (hidden.length !== COUNT) return;
      hidden.forEach((s, i) => {
        window.setTimeout(() => {
          if (Date.now() - lastActivity.current < idleMs || s.mode !== "hidden") return;
          spawn(s, performance.now());
          ensureRunning();
        }, i * randBetween(500, 1400));
      });
    }, 700);

    const onActivity = () => {
      lastActivity.current = Date.now();
      scatter();
    };
    const onMove = (event) => {
      lastActivity.current = Date.now();
      pointer.current = toPage(event);
    };
    const onLeave = () => { pointer.current = null; };

    window.addEventListener("pointerdown", onActivity, true);
    window.addEventListener("keydown", onActivity, true);
    window.addEventListener("wheel", onActivity, { capture: true, passive: true });
    window.addEventListener("pointermove", onMove, { passive: true });
    document.addEventListener("pointerleave", onLeave);
    return () => {
      window.clearInterval(idleCheck);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      window.removeEventListener("pointerdown", onActivity, true);
      window.removeEventListener("keydown", onActivity, true);
      window.removeEventListener("wheel", onActivity, { capture: true });
      window.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerleave", onLeave);
    };
  }, [enabled, idleMs, pageRef, radius, storeApi]);

  // ─── Render ─────────────────────────────────────────────────────────────
  const now = performance.now();
  const visible = sprites.current.filter((s) => s.mode !== "hidden" && network[s.seg]);
  if (!enabled || !visible.length) return null;

  return (
    <svg
      aria-hidden="true"
      data-ink-sprites="1"
      width={pageWidth}
      height={pageHeight}
      style={{ position: "absolute", left: 0, top: 0, zIndex: 57, pointerEvents: "none", overflow: "visible" }}
    >
      {visible.map((s) => {
        const seg = network[s.seg];
        const { x, y } = pointOn(seg, Math.max(seg.start, Math.min(seg.end, s.t)));
        const age = now - (s.modeStart || now);
        let scale = 1;
        let opacity = 1;
        if (s.mode === "emerging") scale = Math.min(1, age / EMERGE_MS);
        if (s.mode === "fleeing") {
          const k = Math.min(1, age / FLEE_MS);
          scale = 1 - k * 0.6;
          opacity = 1 - k;
        }
        const walking = s.mode === "walking" || s.mode === "fleeing";
        const phase = (now / 1000) * (s.mode === "fleeing" ? 30 : 14) + s.seed;
        const bob = walking ? Math.abs(Math.sin(phase)) * radius * 0.35 : 0;
        const squash = walking ? 1 + Math.sin(phase * 2) * 0.06 : 1 + Math.sin(now / 900 + s.seed) * 0.025;
        const blinking = now < (s.blinkUntil || 0);
        const look = s.look || { x: 0, y: 0 };
        const eyeR = radius * 0.34;
        const pupilR = radius * 0.16;
        const legLift = walking ? Math.sin(phase) * radius * 0.25 : 0;
        return (
          <g key={s.id} transform={`translate(${x} ${y - bob})`} opacity={opacity}>
            <g transform={`scale(${scale * (2 - squash)} ${scale * squash})`}>
              {/* Tiny legs while walking */}
              {walking && (
                <g stroke="#1a1a1a" strokeWidth={radius * 0.16} strokeLinecap="round">
                  <line x1={-radius * 0.35} y1={radius * 0.8} x2={-radius * 0.4} y2={radius * 1.3 - legLift} />
                  <line x1={radius * 0.35} y1={radius * 0.8} x2={radius * 0.4} y2={radius * 1.3 + legLift} />
                </g>
              )}
              <path d={fuzz[s.id]} fill="#1a1a1a" />
              <circle r={radius * 0.9} fill="#1a1a1a" />
              {[-1, 1].map((side) => (
                <g key={side} transform={`translate(${side * radius * 0.38} ${-radius * 0.12})`}>
                  <ellipse rx={eyeR} ry={blinking ? eyeR * 0.12 : eyeR} fill="#fffdf6" />
                  {!blinking && (
                    <circle cx={look.x * eyeR * 0.45} cy={look.y * eyeR * 0.45} r={pupilR} fill="#1a1a1a" />
                  )}
                </g>
              ))}
            </g>
          </g>
        );
      })}
    </svg>
  );
};

export default InkSprites;
